import { ActionTree, GetterTree, MutationTree } from 'vuex';
import _ from 'lodash';
import { Table, ColumnsSetting } from '@/main/utils/table';
import { BaseApi } from '@/main/baseApi';

import { storesMap } from '../../registry';
import { PageModule, RootStore } from '../../@types/store.d';
import { ListStore, ListStoreProps, RowDetail } from '../../@types/list.d';
import { DropdownList } from '../../@types/dropdown.d';
import { listName } from '../../listEditTypes';
import { listEditGetters } from '../getters';
import { pageStoreBuilder } from '../page/builder';
import { listActions } from './actions';
import { mutations } from './mutations';

export function listStoreBuilder<
	Provider extends BaseApi,
	Filter extends object,
	TDropdownList extends DropdownList,
	TRowDetail extends RowDetail | null,
	LocalState extends object,
	ExtraActions extends ActionTree<ListStore<Filter, TDropdownList, LocalState, TRowDetail, Props>, RootStore>,
	ExtraMutations extends MutationTree<ListStore<Filter, TDropdownList, LocalState, TRowDetail, Props>>,
	ExtraGetters extends GetterTree<ListStore<Filter, TDropdownList, LocalState, TRowDetail, Props>, RootStore>,
	Props extends ListStoreProps,
>({
	name,
	apiProvider,
	crumbLabel,
	filter,
	tableSetting,
	dropdownList,
	rowDetail,
	localState,
	extraActions,
	extraMutations,
	extraGetters,
	props,
}: {
	name: string,
	apiProvider: Provider,
	crumbLabel: string,
	filter: Filter,
	tableSetting: {	isEditable?: boolean, columnSettings?: Record<string, ColumnsSetting> | null, defaultRowModel?: Record<string, any>, computeHashOnLoad?: boolean },
	dropdownList: TDropdownList,
	rowDetail: TRowDetail,
	localState: LocalState,
	extraActions: ExtraActions,
	extraMutations: ExtraMutations,
	extraGetters: ExtraGetters,
	props: Props,
}) {
	const pageStore = pageStoreBuilder({
		name: listName(name),
		crumbLabel,
		dropdownList,
		localState,
		props,
	});

	const module: PageModule<
	ListStore<Filter, TDropdownList, LocalState, TRowDetail, Props>,
	ExtraActions & typeof pageStore.actions & ReturnType<typeof listActions>,
	ExtraMutations & typeof pageStore.mutations & typeof mutations,
	ExtraGetters & typeof pageStore.getters & typeof listEditGetters
	> = {
		namespaced: true,
		actions: { ...pageStore.actions, ...listActions(apiProvider), ...extraActions },
		getters: { ...pageStore.getters, ...listEditGetters, ...extraGetters },
		mutations: { ...pageStore.mutations, ...mutations, ...extraMutations },
		state(): ListStore<Filter, TDropdownList, LocalState, TRowDetail, Props> {
			return {
				...pageStore.state(),
				table: new Table(tableSetting),
				filter: _.cloneDeep(filter),
				rowDetail: _.cloneDeep(rowDetail),
				init: Object.freeze({
					filter: _.cloneDeep(filter),
					localState: _.cloneDeep(localState),
				}),
			};
		},
	};

	storesMap.set(listName(name), _.cloneDeep(module));

	return module;
}
